import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Menu, MenuItem } from "@mui/material";
import { getSchema } from "oschema";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { DatabaseCollectionDto } from "../lib/generated/api-client";
import { menuItemSx, menuPaperSx } from "../lib/menu-chrome";
import {
  Field,
  columnRank,
  defaultsForCreate,
  fieldLabel,
  isCreateDrawerPrimary,
  isReadOnlyField,
  isReadOnlyOnCreate,
  isSecretField,
} from "../lib/schema-field";
import {
  FieldErrors,
  buildPayload,
  dirtyFieldNames,
  validateRecord,
} from "../lib/validate-record";
import { DynamicFormField } from "./DynamicFormField";
import { RelatedRecords } from "./RelatedRecords";
import { Button } from "./ui/Button";

type Props = {
  fields?: Field[];
  collection?: DatabaseCollectionDto;
  onSubmit?: (data: any) => void | Promise<void>;
  onChange?: (data: any) => void;
  samples?: any[];
  schema?: Field[];
  model?: any;
  onCancel?: () => void;
  onDelete?: () => void;
  /** Edit-only: copy writable fields into a new create drawer. */
  onDuplicate?: () => void;
  debugMode?: boolean;
  submitting?: boolean;
  submitError?: string | null;
  /** Server write errors keyed by schema field name. Win over client errors. */
  serverFieldErrors?: FieldErrors;
  /** Demo datasource: the form is fully usable, but nothing is written. */
  demoMode?: boolean;
  /**
   * Explicit create vs update. Prefill for Duplicate still has keys, so
   * callers must not let `Object.keys(model).length` decide this.
   */
  isUpdate?: boolean;
  /**
   * Keep `schema` order instead of ranking columns. A generated form already
   * carries a deliberate field order (LLM choice first, then the required
   * columns it omitted); collection drawers get no such order and still rank.
   */
  preserveFieldOrder?: boolean;
  /** When set, the action row is portalled into this node (a modal footer). */
  footerSlot?: HTMLElement | null;
  onDirtyChange?: (dirty: boolean) => void;
  /** Create-button copy, e.g. "Create task". Update still says Save changes. */
  createLabel?: string;
  /** Prev/next for an open row. Create and Duplicate as new omit this. */
  rowNav?: {
    index: number;
    total: number;
    canPrev?: boolean;
    canNext?: boolean;
    busy?: boolean;
    onPrev: () => void;
    onNext: () => void;
  };
};

export type FooterState = {
  isUpdate: boolean;
  dirty: boolean;
  valid: boolean;
  submitting: boolean;
  changedCount: number;
  submit: (addAnother?: boolean) => void;
  reset: () => void;
};

const FALLBACK_PRIMARY_COUNT = 4;

/** Seed defaults and prefill, but never expose stored secrets in the form. */
const seedForm = (model: any, fields: Field[], isUpdate: boolean) => {
  const base = model ?? {};
  const seeded = isUpdate ? { ...base } : { ...defaultsForCreate(fields), ...base };
  for (const field of fields) {
    if (isSecretField(field)) seeded[field.name] = "";
  }
  return seeded;
};

type ExtraKind = "extra" | "system";

const FormFieldGrid: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="grid grid-cols-1 gap-x-5 gap-y-3.5 sm:grid-cols-2">
    {children}
  </div>
);

export const DynamicForm = (props: Props) => {
  const schema = useMemo<Field[]>(() => {
    if (props.schema?.length) return props.schema;
    if (props.fields?.length) return props.fields;
    if (props.samples?.length) return getSchema(props.samples) as Field[];
    if (Object.keys(props.model || {}).length) {
      return getSchema([props.model]) as Field[];
    }
    return [];
  }, [props.schema, props.fields, props.samples, props.model]);

  const isUpdate =
    props.isUpdate ??
    (!!props.model && Object.keys(props.model).length > 0);

  const [form, setForm] = useState<any>(() =>
    seedForm(props.model, schema, isUpdate)
  );
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showExtras, setShowExtras] = useState(false);

  /**
   * Creating a row should not ask for the columns the database fills in. Editing
   * one still shows them, read-only, because seeing the id you are editing is
   * the whole point.
   */
  const fields = useMemo(() => {
    const visible = isUpdate
      ? schema
      : schema.filter((field) => !isReadOnlyOnCreate(field));
    if (props.preserveFieldOrder) return visible;
    return [...visible]
      .map((field, index) => ({ field, index }))
      .sort((a, b) => {
        const ra = columnRank(a.field);
        const rb = columnRank(b.field);
        return ra === rb ? a.index - b.index : ra - rb;
      })
      .map(({ field }) => field);
  }, [schema, isUpdate, props.preserveFieldOrder]);

  /**
   * Create leads with the row's identity, required values, state, scalars and
   * prose. Remaining audit, structured and secret fields stay in the payload
   * and validation, but sit behind the extra-fields disclosure until opened.
   * Update leads with writable columns and parks id / audit timestamps behind
   * "Show N system fields" — the drawer subtitle already names the row.
   */
  const { primaryFields, extraFields, extraKind } = useMemo(() => {
    // A generated form is already a curated shortlist, so hiding half of it
    // behind "Show N more fields" buries fields the author deliberately chose.
    // Show the overlay list as-is; no split, no disclosure.
    if (props.preserveFieldOrder) {
      return {
        primaryFields: fields,
        extraFields: [] as Field[],
        extraKind: "extra" as ExtraKind,
      };
    }
    if (isUpdate) {
      const extra = fields.filter(isReadOnlyField);
      const primary = fields.filter((field) => !isReadOnlyField(field));
      if (primary.length === 0) {
        return {
          primaryFields: fields,
          extraFields: [] as Field[],
          extraKind: "extra" as ExtraKind,
        };
      }
      return {
        primaryFields: primary,
        extraFields: extra,
        extraKind: "system" as ExtraKind,
      };
    }
    let primary = fields.filter(isCreateDrawerPrimary);
    if (primary.length === 0) {
      // Last resort for schemas with no ordinary create fields: retain the
      // existing small first slice rather than opening an empty primary grid.
      primary = fields.slice(0, FALLBACK_PRIMARY_COUNT);
    }
    const primaryNames = new Set(primary.map((field) => field.name));
    const extra = fields.filter((field) => !primaryNames.has(field.name));
    return {
      primaryFields: extra.length <= 1 ? [...primary, ...extra] : primary,
      extraFields: extra.length <= 1 ? [] : extra,
      extraKind: "extra" as ExtraKind,
    };
  }, [fields, isUpdate, props.preserveFieldOrder]);

  useEffect(() => {
    setForm(seedForm(props.model, schema, isUpdate));
    setTouched({});
    setSubmitAttempted(false);
    setShowExtras(false);
    // Defaults come from schema at the moment the row/mode changes. Listing
    // `schema` would reset in-progress edits if the parent passed a new array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.model, isUpdate]);

  const clientErrors: FieldErrors = useMemo(
    () => validateRecord(fields, form, isUpdate),
    [fields, form, isUpdate]
  );
  const errors: FieldErrors = useMemo(
    () => ({ ...clientErrors, ...(props.serverFieldErrors ?? {}) }),
    [clientErrors, props.serverFieldErrors]
  );

  const changed = useMemo(
    () => buildPayload(fields, form, isUpdate ? props.model : null),
    [fields, form, isUpdate, props.model]
  );
  const baseline = useMemo(
    () => seedForm(props.model, schema, isUpdate),
    [props.model, schema, isUpdate]
  );
  const changedCount = useMemo(
    () => dirtyFieldNames(fields, form, baseline, isUpdate).length,
    [fields, form, baseline, isUpdate]
  );
  const dirty = changedCount > 0;

  useEffect(() => {
    props.onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const onChangeForm = useCallback(
    (name: string, val: any) => {
      setTouched((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
      setForm((prev: any) => {
        const next = { ...prev, [name]: val };
        props.onChange?.(next);
        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange]
  );

  useEffect(() => {
    if (extraFields.length === 0) return;
    const server = props.serverFieldErrors ?? {};
    const hiddenError =
      extraFields.some((field) => errors[field.name]) &&
      (submitAttempted || extraFields.some((field) => server[field.name]));
    if (hiddenError) setShowExtras(true);
  }, [submitAttempted, extraFields, errors, props.serverFieldErrors]);

  useEffect(() => {
    const first = Object.keys(props.serverFieldErrors ?? {})[0];
    if (!first) return;
    const focus = () => {
      const target = document.getElementById(`field-${first}`);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus?.();
    };
    requestAnimationFrame(focus);
  }, [props.serverFieldErrors]);

  const submit = useCallback(async (addAnother = false) => {
    setSubmitAttempted(true);
    const invalid = Object.keys(errors);
    if (invalid.length > 0) {
      const hiddenExtraError =
        !showExtras && extraFields.some((field) => errors[field.name]);
      if (hiddenExtraError) setShowExtras(true);
      const focusFirst = () => {
        const target = document.getElementById(`field-${invalid[0]}`);
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
        target?.focus?.();
      };
      if (hiddenExtraError) {
        requestAnimationFrame(focusFirst);
      } else {
        focusFirst();
      }
      return;
    }
    await props.onSubmit?.({
      ...form,
      __changed: changed,
      ...(addAnother ? { __addAnother: true } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, form, changed, extraFields, showExtras, props.onSubmit]);

  const reset = useCallback(() => {
    setForm(seedForm(props.model, schema, isUpdate));
    setTouched({});
    setSubmitAttempted(false);
  }, [props.model, schema, isUpdate]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
        return;
      }
      if (event.key.toLowerCase() !== "s") return;
      event.preventDefault();
      if (props.submitting) return;
      if (isUpdate && !dirty) return;
      void submit();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dirty, isUpdate, props.submitting, submit]);

  const footerState: FooterState = {
    isUpdate,
    dirty,
    valid: Object.keys(errors).length === 0,
    submitting: !!props.submitting,
    changedCount,
    submit,
    reset,
  };

  const footer = (
    <DynamicFormFooter
      state={footerState}
      onCancel={props.onCancel}
      onDelete={props.onDelete}
      onDuplicate={props.onDuplicate}
      demoMode={props.demoMode}
      createLabel={props.createLabel}
      rowNav={props.isUpdate === false ? undefined : props.rowNav}
    />
  );
  const footerNode = props.footerSlot
    ? createPortal(footer, props.footerSlot)
    : footer;

  const renderField = (field: Field) => {
    const serverError = props.serverFieldErrors?.[field.name];
    const showError = submitAttempted || touched[field.name] || !!serverError;
    return (
      <DynamicFormField
        key={field.name}
        field={field}
        model={form}
        onChangeForm={onChangeForm}
        collection={props.collection}
        debugMode={props.debugMode}
        demoMode={props.demoMode}
        error={showError ? errors[field.name] : undefined}
        isUpdate={isUpdate}
      />
    );
  };

  if (!fields.length) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-od-border bg-od-surface-2 p-4 text-sm text-od-muted">
        <ErrorOutlineIcon sx={{ fontSize: 18 }} />
        No schema is available for this collection yet. Re-run the analysis to
        generate one.
      </div>
    );
  }

  const alertError =
    props.submitError &&
    !fields.some(
      (field) => props.serverFieldErrors?.[field.name] === props.submitError
    )
      ? props.submitError
      : null;

  return (
    <div className="flex flex-col gap-4">
      {alertError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border px-3 py-2 text-xs"
          style={{
            color: "var(--od-danger)",
            borderColor: "color-mix(in srgb, var(--od-danger) 40%, transparent)",
            backgroundColor:
              "color-mix(in srgb, var(--od-danger) 10%, transparent)",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 15, mt: "1px" }} />
          <span>{alertError}</span>
        </div>
      )}

      <FormFieldGrid>{primaryFields.map(renderField)}</FormFieldGrid>

      {extraFields.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Button
            aria-expanded={showExtras}
            onClick={() => setShowExtras((open) => !open)}
            variant="link" size="sm" className="self-start"
          >
            {showExtras
              ? extraKind === "system"
                ? "Hide system fields"
                : "Hide extra fields"
              : extraKind === "system"
              ? `Show ${extraFields.length} system ${
                  extraFields.length === 1 ? "field" : "fields"
                }`
              : `Show ${extraFields.length} extra ${
                  extraFields.length === 1 ? "field" : "fields"
                }`}
          </Button>
          {!showExtras && (
            <p className="text-xs text-od-muted">
              {extraFields.map(fieldLabel).join(" · ")}
            </p>
          )}
        </div>
      )}

      {showExtras && extraFields.length > 0 && (
        <div className="flex flex-col gap-2">
          {extraKind === "system" && (
            <p className="text-xs font-medium uppercase tracking-wide text-od-muted">
              System fields
            </p>
          )}
          <FormFieldGrid>{extraFields.map(renderField)}</FormFieldGrid>
        </div>
      )}

      {isUpdate && (props.collection?.targetRelations?.length ?? 0) > 0 && (
        <RelatedRecords
          collection={props.collection}
          model={form}
          demoMode={props.demoMode}
        />
      )}

      {footerNode}

      {props.debugMode && (
        <pre className="m-0 max-h-64 overflow-auto rounded-lg border border-od-border bg-od-surface-2 p-3 text-xs text-od-muted">
          {JSON.stringify({ changed, errors }, null, 2)}
        </pre>
      )}
    </div>
  );
};

/** Standard action row, rendered into the modal footer. */
export const DynamicFormFooter: React.FC<{
  state: FooterState;
  onCancel?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  demoMode?: boolean;
  createLabel?: string;
  rowNav?: {
    index: number;
    total: number;
    canPrev?: boolean;
    canNext?: boolean;
    busy?: boolean;
    onPrev: () => void;
    onNext: () => void;
  };
}> = ({
  state,
  onCancel,
  onDelete,
  onDuplicate,
  demoMode,
  createLabel,
  rowNav,
}) => {
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const showNav = state.isUpdate && rowNav && rowNav.total > 0;
  const canPrev = !!rowNav && (rowNav.canPrev ?? rowNav.index > 0);
  const canNext =
    !!rowNav && (rowNav.canNext ?? rowNav.index < rowNav.total - 1);
  const showMobileMore =
    !!showNav ||
    (state.isUpdate && !!onDelete && !demoMode) ||
    (state.isUpdate && !!onDuplicate) ||
    (state.dirty && !demoMode) ||
    (!state.isUpdate && !demoMode);

  return (
    <div className="mt-1 flex flex-nowrap items-center justify-end gap-2 sm:flex-wrap">
      <div className="mr-auto hidden items-center gap-2 sm:flex">
        {showNav && rowNav && (
          <div className="flex items-center gap-1.5">
            <Button
              onClick={rowNav.onPrev}
              disabled={!canPrev || state.submitting || rowNav.busy}
              aria-label="Previous record"
              variant="secondary" size="sm"
            >
              Prev
            </Button>
            <span className="min-w-[2.75rem] text-center text-xs tabular-nums text-od-muted">
              {rowNav.index + 1} / {rowNav.total}
            </span>
            <Button
              onClick={rowNav.onNext}
              disabled={!canNext || state.submitting || rowNav.busy}
              aria-label="Next record"
              variant="secondary" size="sm"
            >
              Next
            </Button>
          </div>
        )}
        {state.isUpdate && onDelete && !demoMode && (
          <Button
            onClick={onDelete}
            variant="danger" size="sm"
          >
            Delete
          </Button>
        )}
        {state.isUpdate && onDuplicate && (
          <Button
            onClick={onDuplicate}
            disabled={state.submitting}
            variant="secondary" size="sm"
          >
            Duplicate as new
          </Button>
        )}
        <span className="text-xs text-od-muted">
          {demoMode
            ? "Demo only. Nothing is written."
            : state.dirty
            ? `${state.changedCount} unsaved ${
                state.changedCount === 1 ? "change" : "changes"
              }`
            : state.isUpdate
            ? "No changes yet"
            : ""}
        </span>
      </div>

      <div className="flex min-w-0 items-center gap-2">
        {showMobileMore && (
          <>
            <Button
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={!!moreAnchor}
              onClick={(event) => setMoreAnchor(event.currentTarget)}
              variant="secondary" size="icon" className="od-form-more text-od-muted sm:hidden"
            >
              <MoreHorizIcon sx={{ fontSize: 18 }} />
            </Button>
            <Menu
              anchorEl={moreAnchor}
              open={!!moreAnchor}
              onClose={() => setMoreAnchor(null)}
              slotProps={{ paper: { sx: menuPaperSx } }}
              MenuListProps={{ dense: true, sx: { py: 0 } }}
            >
              {showNav && rowNav && (
                <MenuItem
                  dense
                  disabled={!canPrev || state.submitting || rowNav.busy}
                  onClick={() => {
                    setMoreAnchor(null);
                    rowNav.onPrev();
                  }}
                  sx={menuItemSx}
                >
                  Previous record ({rowNav.index + 1} / {rowNav.total})
                </MenuItem>
              )}
              {showNav && rowNav && (
                <MenuItem
                  dense
                  disabled={!canNext || state.submitting || rowNav.busy}
                  onClick={() => {
                    setMoreAnchor(null);
                    rowNav.onNext();
                  }}
                  sx={menuItemSx}
                >
                  Next record ({rowNav.index + 1} / {rowNav.total})
                </MenuItem>
              )}
              {state.isUpdate && onDelete && !demoMode && (
                <MenuItem
                  dense
                  onClick={() => {
                    setMoreAnchor(null);
                    onDelete();
                  }}
                  sx={{ ...menuItemSx, color: "var(--od-danger)" }}
                >
                  Delete
                </MenuItem>
              )}
              {state.isUpdate && onDuplicate && (
                <MenuItem
                  dense
                  disabled={state.submitting}
                  onClick={() => {
                    setMoreAnchor(null);
                    onDuplicate();
                  }}
                  sx={menuItemSx}
                >
                  Duplicate as new
                </MenuItem>
              )}
              {state.dirty && !demoMode && (
                <MenuItem
                  dense
                  onClick={() => {
                    setMoreAnchor(null);
                    state.reset();
                  }}
                  sx={menuItemSx}
                >
                  Revert
                </MenuItem>
              )}
              {!state.isUpdate && !demoMode && (
                <MenuItem
                  dense
                  disabled={state.submitting}
                  onClick={() => {
                    setMoreAnchor(null);
                    state.submit(true);
                  }}
                  sx={menuItemSx}
                >
                  Create and add another
                </MenuItem>
              )}
            </Menu>
          </>
        )}
        {state.dirty && !demoMode && (
          <Button
            onClick={state.reset}
            variant="ghost" size="sm" className="hidden sm:inline-flex"
          >
            Revert
          </Button>
        )}
        <Button
          onClick={onCancel}
          variant="secondary" size="sm"
        >
          Cancel
        </Button>
        {!state.isUpdate && !demoMode && (
          <Button
            onClick={() => state.submit(true)}
            disabled={state.submitting}
            variant="secondary" size="sm" className="hidden sm:inline-flex"
          >
            Create and add another
          </Button>
        )}
        {!demoMode && (
        <Button
          onClick={() => state.submit()}
          disabled={state.isUpdate && !state.dirty}
          loading={state.submitting}
          variant="primary" size="sm"
        >
          {state.isUpdate ? "Save changes" : createLabel || "Create record"}
        </Button>
        )}
      </div>
    </div>
  );
};
